import {
  Attribute,
  attributeFeaturesLabel,
  AttributeGroup,
  Bucket,
  findLayerAttribute,
  MapLayer,
} from "@cartographerio/atlas-map";
import { Feature } from "@cartographerio/geometry";
import { chakra, Radio, Stack } from "@chakra-ui/react";
import React, { ChangeEvent, ReactElement, useCallback } from "react";
import { FaChevronRight } from "react-icons/fa";
import { Dict } from "../../hooks/useDict";
import FeaturesSelectedCount from "./FeaturesSelectedCount";
import InspectorBox from "./InspectorBox";
import InspectorButton from "./InspectorButton";
import InspectorHeading from "./InspectorHeading";
import InspectorItem from "./InspectorItem";
import InspectorTitle from "./InspectorTitle";

type OnAttributeClick = (attr: Attribute, focus: boolean) => void;
type OnAttributeIdClick = (attrId: string, focus: boolean) => void;

export interface LayerInspectorProps {
  layer: MapLayer;
  selectedAttribute: Attribute;
  buckets: Dict<Bucket[]>;
  onAttributeClick: OnAttributeClick;
  onBackClick: () => void;
  selectedFeatures: Feature[];
  onClearFeatureSelection: () => void;
}

export default function LayerInspector(
  props: LayerInspectorProps
): ReactElement {
  const {
    layer,
    selectedAttribute,
    buckets,
    selectedFeatures,
    onAttributeClick,
    onClearFeatureSelection,
    onBackClick,
  } = props;

  const selectedAttributeId = selectedAttribute.attributeId;

  const handleAttributeIdClick = useCallback(
    (attributeId: string, focus: boolean) => {
      const attr = findLayerAttribute(layer, attributeId);
      if (attr != null) {
        onAttributeClick(attr, focus);
      }
    },
    [layer, onAttributeClick]
  );

  return (
    <InspectorBox>
      <InspectorTitle
        backLabel="Back to layer selection"
        onBackClick={onBackClick}
      >
        Layer
      </InspectorTitle>

      <InspectorHeading>{layer.title}</InspectorHeading>

      <FeaturesSelectedCount
        count={selectedFeatures.length}
        onClear={onClearFeatureSelection}
      />

      <Stack spacing="4">
        {layer.attributes.map((group, index) => (
          <AttributeGroupItem
            key={index}
            group={group}
            buckets={buckets}
            selectedAttributeId={selectedAttributeId}
            selectedFeatures={selectedFeatures}
            onAttributeIdClick={handleAttributeIdClick}
          />
        ))}
      </Stack>
    </InspectorBox>
  );
}

interface AttributeGroupItemProps {
  group: AttributeGroup;
  buckets: Dict<Bucket[]>;
  selectedAttributeId?: string;
  selectedFeatures: Feature[];
  onAttributeIdClick: OnAttributeIdClick;
}

function AttributeGroupItem(props: AttributeGroupItemProps): ReactElement {
  const {
    group,
    buckets,
    selectedAttributeId,
    selectedFeatures,
    onAttributeIdClick,
  } = props;

  return (
    <Stack spacing="4">
      <InspectorHeading>{group.label}</InspectorHeading>

      {group.attributes.map(attr => (
        <AttributeItem
          key={attr.attributeId}
          attr={attr}
          buckets={buckets}
          selectedAttributeId={selectedAttributeId}
          selectedFeatures={selectedFeatures}
          onAttributeIdClick={onAttributeIdClick}
        />
      ))}
    </Stack>
  );
}

interface AttributeItemProps {
  attr: Attribute;
  buckets: Dict<Bucket[]>;
  selectedAttributeId?: string;
  selectedFeatures: Feature[];
  onAttributeIdClick: OnAttributeIdClick;
}

function AttributeItem(props: AttributeItemProps): ReactElement {
  const {
    attr,
    buckets,
    selectedAttributeId,
    selectedFeatures,
    onAttributeIdClick,
  } = props;

  const attributeId = attr.attributeId;
  const label = attr.label;

  const values = attributeFeaturesLabel(
    attr,
    buckets[attributeId],
    selectedFeatures
  );

  const handleRadioClick = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      evt.stopPropagation();
      evt.preventDefault();
      onAttributeIdClick(attributeId, false);
    },
    [attributeId, onAttributeIdClick]
  );

  const button = (
    <InspectorButton
      as={FaChevronRight}
      ariaLabel="View attribute"
      onClick={() => onAttributeIdClick(attributeId, true)}
    />
  );

  return (
    <InspectorItem right={button}>
      <Radio
        flex="1"
        isChecked={attributeId === selectedAttributeId}
        onChange={handleRadioClick}
      >
        <chakra.div maxWidth="100%" fontSize="sm">
          {label}
        </chakra.div>
        {values && (
          <chakra.div maxWidth="100%" fontSize="sm" color="gray.500">
            {values}
          </chakra.div>
        )}
      </Radio>
    </InspectorItem>
  );
}
